import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';

const EyeIcon = ({ off }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {off ? (
      <>
        <path d="M17.94 17.94A10.94 10.94 0 0112 20C5 20 1 12 1 12a19.4 19.4 0 015.06-6.13M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.4 19.4 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const boxStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 32, width: 340 };
const labelStyle = { display: 'block', marginBottom: 12 };
const inputStyle = { width: '100%', marginTop: 4 };

function ForgotPassword({ onBack }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api('/auth/forgot-password', { method: 'POST', body: { email } });
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong — try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={boxStyle}>
        <h1 style={{ marginTop: 0 }}>Reset password</h1>
        {sent ? (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              If an account exists for <strong>{email}</strong>, a reset link has been sent. It expires in 1 hour.
            </p>
            <button type="button" onClick={onBack} style={{ width: '100%' }}>Back to login</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label style={labelStyle}>
              Email
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} required />
            </label>
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <button type="submit" disabled={busy} style={{ width: '100%', marginBottom: 10 }}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <button type="button" onClick={onBack} style={{ width: '100%', background: 'none', border: 'none', color: 'var(--text-dim)' }}>
              Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Also where a brand-new tenant's "set up your account" invite link
// lands — same screen, same endpoint, just the first-ever password
// instead of a replacement one. App.jsx clears any session already stored
// in the browser when the URL has ?reset_token=, so this screen is always
// what an invite link shows.
function ResetPassword({ token, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api('/auth/reset-password', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong — try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={boxStyle}>
        <h1 style={{ marginTop: 0 }}>Set your password</h1>
        {done ? (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Password set — you can log in now.</p>
            <button type="button" onClick={onDone} style={{ width: '100%' }}>Go to login</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label style={labelStyle}>
              New password
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} required minLength={8} />
            </label>
            <label style={labelStyle}>
              Confirm password
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} required minLength={8} />
            </label>
            {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}
            <button type="submit" disabled={busy} style={{ width: '100%' }}>{busy ? 'Saving…' : 'Set password'}</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Login({ onLogin, notice = '' }) {
  // No router mounted yet at this point (App.jsx renders Login before
  // BrowserRouter/Routes) — a reset/invite link points at the root URL
  // with ?reset_token=..., which any static host serves as index.html
  // without needing a rewrite rule for a distinct path.
  const [screen, setScreen] = useState(() => {
    const token = new URLSearchParams(window.location.search).get('reset_token');
    return token ? 'reset' : 'login';
  });
  const resetToken = new URLSearchParams(window.location.search).get('reset_token');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Drop the token from the URL as soon as it's read (not on unmount —
  // ResetPassword above switches between its own form/done states inside
  // this same Login instance, it never actually unmounts), so refreshing
  // the page or bookmarking it doesn't re-trigger reset mode with an
  // already-used token.
  useEffect(() => {
    if (screen === 'reset' && resetToken) window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError('');
    try {
      const result = await api('/auth/login', { method: 'POST', body: { email, password } });
      if (result.user.role === 'platform_admin') {
        setError('Admin accounts log in at stsgp.com, not here.');
        return;
      }
      localStorage.setItem('loadline_token', result.token);
      onLogin(result.user, result.features);
    } catch (err) {
      setError(err.message || 'Wrong email or password.');
    }
  }

  if (screen === 'forgot') return <ForgotPassword onBack={() => setScreen('login')} />;
  if (screen === 'reset') return <ResetPassword token={resetToken} onDone={() => setScreen('login')} />;

  return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh'}}>
      <form onSubmit={submit} style={boxStyle}>
        <h1 style={{marginTop:0}}>TruckFlow</h1>
        {notice && (
          <p role="status" style={{
            background: 'rgba(242,169,59,.12)', border: '1px solid rgba(242,169,59,.45)', color: 'var(--amber)',
            borderRadius: 6, padding: '8px 10px', fontSize: 13, marginTop: 0,
          }}>{notice}</p>
        )}
        <label style={labelStyle}>
          Email
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                 style={inputStyle} required />
        </label>
        <label style={{display:'block', marginBottom:6}}>
          Password
          <div style={{ position: 'relative', marginTop: 4 }}>
            <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                   style={{ width: '100%', paddingRight: 34 }} required />
            <button type="button" onClick={() => setShowPassword(s => !s)}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', padding: 4, display: 'flex',
                      alignItems: 'center', color: 'var(--text-dim)',
                    }}>
              <EyeIcon off={showPassword} />
            </button>
          </div>
        </label>
        <div style={{ textAlign: 'right', marginBottom: 16 }}>
          <button type="button" onClick={() => setScreen('forgot')}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: 'var(--text-dim)' }}>
            Forgot password?
          </button>
        </div>
        {error && <p style={{color:'var(--red)', fontSize:13}}>{error}</p>}
        <button type="submit" style={{width:'100%'}}>Log in</button>
      </form>
    </div>
  );
}
